import { useCallback } from 'react';
import { makeSettingsChangedEvent } from '../../domain/events';
import { RapidSettings } from '../../domain/rapid';
import { eventStore } from '../../services/EventStore';
import { GameState } from '../../types/game';
import { GameAction } from '../gameReducer';

interface UseSettingsSyncDeps {
  gameState: GameState;
  dispatch: React.Dispatch<GameAction>;
  isHost: boolean;
  currentClientPlayerId: string | null;
}

// Rules:
// 1. Host ONLINE -> emit SETTINGS_CHANGED event (optimistic update, rollback on failure)
// 2. Non-host ONLINE -> ignore; settings stay host-authoritative
// 3. REAL mode -> pure local dispatch
export function useSettingsSync({ gameState, dispatch, isHost, currentClientPlayerId }: UseSettingsSyncDeps) {
  const toggle = useCallback((key: 'tunnels' | 'rescueByFive' | 'zones' | 'zonesAvoidTunnels') => {
    const actionType = key === 'tunnels' ? 'TOGGLE_TUNNELS'
      : key === 'zones' ? 'TOGGLE_ZONES'
      : key === 'zonesAvoidTunnels' ? 'TOGGLE_ZONES_AVOID_TUNNELS'
      : 'TOGGLE_RESCUE_BY_FIVE';
    if (gameState.mode === 'REAL') {
      dispatch({ type: actionType });
      return;
    }
    if (!isHost) return;
    // ONLINE host path
    dispatch({ type: actionType });
    const newSettings: Parameters<typeof makeSettingsChangedEvent>[1] = {
      ...gameState.settings,
      [key]: !gameState.settings[key],
    };
    if (!currentClientPlayerId) return; // cannot emit without client identity
    (async () => {
      try {
        const ev = makeSettingsChangedEvent(currentClientPlayerId, newSettings);
        await eventStore.appendEvent(gameState.id, ev);
      } catch (_e) {
        // Rollback on failure
        dispatch({ type: actionType });
      }
    })();
  }, [gameState, dispatch, isHost, currentClientPlayerId]);

  const updateRapidSettings = useCallback((rapid: RapidSettings) => {
    if (gameState.mode === 'REAL') {
      dispatch({ type: 'UPDATE_SETTINGS', settings: { ...gameState.settings, rapid } });
      return;
    }
    if (!isHost) return;
    const previousRapid = gameState.settings.rapid;
    dispatch({ type: 'UPDATE_SETTINGS', settings: { ...gameState.settings, rapid } });
    if (!currentClientPlayerId) return;
    (async () => {
      try {
        const ev = makeSettingsChangedEvent(currentClientPlayerId, { ...gameState.settings, rapid });
        await eventStore.appendEvent(gameState.id, ev);
      } catch (_e) {
        dispatch({
          type: 'UPDATE_SETTINGS',
          settings: { ...gameState.settings, rapid: previousRapid },
        });
      }
    })();
  }, [gameState, dispatch, isHost, currentClientPlayerId]);

  return {
    toggleTunnels: () => toggle('tunnels'),
    toggleRescueByFive: () => toggle('rescueByFive'),
    toggleZones: () => toggle('zones'),
    toggleZonesAvoidTunnels: () => toggle('zonesAvoidTunnels'),
    updateRapidSettings,
  };
}

